import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { fetchCategoryTree, type Category } from "../api/categories";
import FeedbackForm from "../pages/feedback/FeedbackForm";
import "./Header.css";
import { useCategoryContext } from "../context/CategoryContext";

/** Collect the id of a category and all of its descendant ids. */
function collectSubtreeIds(category: Category): number[] {
  const ids: number[] = [category.id];
  for (const child of category.children ?? []) {
    ids.push(...collectSubtreeIds(child));
  }
  return ids;
}

/** True when every category in the subtree is present in the selection. */
function isFullySelected(category: Category, selected: Set<number>): boolean {
  return collectSubtreeIds(category).every((id) => selected.has(id));
}

/** True when at least one — but not all — categories in the subtree are selected. */
function isPartiallySelected(category: Category, selected: Set<number>): boolean {
  const ids = collectSubtreeIds(category);
  const count = ids.filter((id) => selected.has(id)).length;
  return count > 0 && count < ids.length;
}

/** Recursive tree node used inside the category picker popup. */
function CategoryTreeNode({
  category,
  depth,
  selected,
  onToggle,
}: {
  category: Category;
  depth: number;
  selected: Set<number>;
  onToggle: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const children = category.children ?? [];
  const hasChildren = children.length > 0;
  const checked = isFullySelected(category, selected);
  const indeterminate = !checked && isPartiallySelected(category, selected);

  return (
    <li className="category-tree-item">
      <div
        className={`category-option${checked ? " selected" : ""}${indeterminate ? " partial" : ""}`}
        style={{ paddingLeft: `${0.7 + depth * 1.05}rem` }}
        role="checkbox"
        aria-checked={indeterminate ? "mixed" : checked}
        tabIndex={0}
        onClick={() => onToggle(category.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(category.id);
          }
        }}
      >
        <button
          type="button"
          className={`category-toggle${hasChildren ? "" : " category-toggle-hidden"}`}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={hasChildren ? expanded : undefined}
          aria-label={expanded ? `Collapse ${category.name}` : `Expand ${category.name}`}
          tabIndex={hasChildren ? 0 : -1}
        >
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <span
          className={`category-checkbox${checked ? " checked" : ""}${indeterminate ? " indeterminate" : ""}`}
          aria-hidden="true"
        ></span>
        <span className="category-option-name">{category.name}</span>
      </div>

      {hasChildren && expanded && (
        <ul className="category-tree-children">
          {children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              category={child}
              depth={depth + 1}
              selected={selected}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Header() {
  const { selectedCategories, setSelectedCategories, updateUrlWithCategories } = useCategoryContext();

  useEffect(() => {
    // When URL changes, sync the context state
    const urlParams = new URLSearchParams(window.location.search);
    const currentCategories = urlParams.get('categoryIds');
    if (currentCategories) {
      const ids = currentCategories.split(',').map(Number).filter(id => !isNaN(id));
      setSelectedCategories(ids);
    } else {
      setSelectedCategories([]);
    }
  }, [setSelectedCategories]);

  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(localStorage.getItem("token")));
  const [categories, setCategories] = useState<Category[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [draftSelectedIds, setDraftSelectedIds] = useState<number[]>(selectedCategories); // Use context state as initial value
  const navigate = useNavigate();

  useEffect(() => {
    // Load the full hierarchical category tree from the DB.
    // Guard against non-array payloads (e.g. an SPA fallback page served
    // instead of JSON) so a bad response can never crash rendering.
    fetchCategoryTree()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Failed to load categories:", error));
  }, []);

  useEffect(() => {
    const syncAuthState = () => setIsLoggedIn(Boolean(localStorage.getItem("token")));
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("auth-state-changed", syncAuthState);
    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("auth-state-changed", syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!isPickerOpen && !isFeedbackOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsPickerOpen(false);
        setIsFeedbackOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPickerOpen, isFeedbackOpen]);

  useEffect(() => {
    if (!isPickerOpen && !isFeedbackOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPickerOpen, isFeedbackOpen]);

  const handleUpload = () => {
    navigate("/upload");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    window.dispatchEvent(new Event("auth-state-changed"));
    navigate("/");
  };

  const openPicker = () => {
    // When opening the picker, sync draft with current selected categories
    setDraftSelectedIds(selectedCategories);
    setIsPickerOpen(true);
  };

  const closePicker = () => {
    setIsPickerOpen(false);
  };

  const handleToggleCategory = (id: number) => {
    setDraftSelectedIds((prev) => {
      const findNode = (nodes: Category[]): Category | undefined => {
        for (const node of nodes) {
          if (node.id === id) return node;
          const found = findNode(node.children ?? []);
          if (found) return found;
        }
        return undefined;
      };
      const node = findNode(categories);
      if (!node) return prev;

      const subtreeIds = collectSubtreeIds(node);
      const selectedSet = new Set(prev);
      if (isFullySelected(node, selectedSet)) {
        subtreeIds.forEach((subId) => selectedSet.delete(subId));
      } else {
        subtreeIds.forEach((subId) => selectedSet.add(subId));
      }
      return Array.from(selectedSet);
    });
  };

  const handleApplyCategories = () => {
    // Update the context state with the new selection
    setSelectedCategories(draftSelectedIds);
    
    // Update URL with the new category IDs
    updateUrlWithCategories(draftSelectedIds);
    
    setIsPickerOpen(false);
  };

  const handleClearCategories = () => {
    setDraftSelectedIds([]);
  };

  const flattenNames = (nodes: Category[], acc: Map<number, string> = new Map()): Map<number, string> => {
    if (!Array.isArray(nodes)) return acc;
    nodes.forEach((n) => {
      acc.set(n.id, n.name);
      flattenNames(n.children ?? [], acc);
    });
    return acc;
  };
  const namesById = flattenNames(categories);
  const appliedCount = draftSelectedIds.length;
  let badgeText: string | null = null;
  if (appliedCount === 1) {
    badgeText = namesById.get(draftSelectedIds[0]) ?? null;
  } else if (appliedCount > 1) {
    badgeText = `${appliedCount} categories`;
  }

  return (
    <header className="site-header">
      <div className="header-inner">
        <div className="header-left">
          <Link to="/" className="brand" aria-label="iFoto home">
            <img className="brand-logo" src="/stillwave-logo.svg" alt="" />
            <span className="brand-text">
              <span className="brand-name">StillWave.art</span>
              <span className="brand-tag">Photography</span>
            </span>
          </Link>

          <button
            type="button"
            className={`btn btn-category${appliedCount > 0 ? " btn-category-active" : ""}`}
            onClick={openPicker}
            aria-haspopup="dialog"
            title="Filter photos by category"
          >
            <svg
              className="btn-icon-svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Categories
            {badgeText && (
              <span className="btn-category-badge">{badgeText}</span>
            )}
          </button>
        </div>

        <nav className="header-actions" aria-label="Main navigation">
          {isLoggedIn ? (
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setIsFeedbackOpen(true)}
                aria-haspopup="dialog"
              >
                Feedback
              </button>
              <button className="btn btn-primary" onClick={handleUpload}>
                <span className="btn-icon" aria-hidden="true">+</span> Upload
              </button>
              <button className="btn btn-ghost" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            <button
              className="btn btn-ghost"
              onClick={() => setIsFeedbackOpen(true)}
              aria-haspopup="dialog"
            >
              Feedback
            </button>
          )}
        </nav>
      </div>

      {isPickerOpen &&
        createPortal(
        <div
          className="category-modal-overlay"
          onClick={closePicker}
        >
          <div
            className="category-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Select categories"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-modal-head">
              <h2>Select Categories</h2>
              <button
                type="button"
                className="category-modal-close"
                onClick={closePicker}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="category-modal-subtitle">
              Choose one or more categories to filter the gallery. Selecting a parent also
              selects its subcategories, then press Ok.
            </p>

            <div className="category-options" role="group" aria-label="Categories">
              <ul className="category-tree">
                {categories.map((category) => (
                  <CategoryTreeNode
                    key={category.id}
                    category={category}
                    depth={0}
                    selected={new Set(draftSelectedIds)}
                    onToggle={handleToggleCategory}
                  />
                ))}
                {categories.length === 0 && (
                  <p className="category-empty">No categories available yet.</p>
                )}
              </ul>
            </div>

            <div className="category-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleClearCategories}
                disabled={draftSelectedIds.length === 0}
              >
                Clear All
              </button>
              <span className="category-modal-spacer" />
              <button type="button" className="btn btn-ghost" onClick={closePicker}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApplyCategories}
              >
                Ok
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isFeedbackOpen &&
        createPortal(
          <div
            className="feedback-modal-overlay"
            onClick={() => setIsFeedbackOpen(false)}
          >
            <div
              className="feedback-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="feedback-modal-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="category-modal-head">
                <h2 id="feedback-modal-title">Send Feedback</h2>
                <button
                  type="button"
                  className="category-modal-close"
                  onClick={() => setIsFeedbackOpen(false)}
                  aria-label="Close feedback form"
                >
                  ✕
                </button>
              </div>
              <p className="category-modal-subtitle">
                Tell us what would make your experience better.
              </p>
              <FeedbackForm
                onCancel={() => setIsFeedbackOpen(false)}
                onSent={() => setIsFeedbackOpen(false)}
              />
            </div>
          </div>,
          document.body
        )}
    </header>
  );
}

export default Header;